import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import { fetchRedirectionParams } from "../../../actions/redirect";
import { setCardData } from "../../../actions/card";
import TestCards from "../../../model/TestCards";
import { FormatAmount } from "../../../utils/Formatter";
import CardInput from "./cardinput/CardInput";
import ExpDateInput from "./expdate/ExpDateInput";
import CardInputValidator from "./cardinput/CardInputValidator";
import ExpDateValidator from "./expdate/ExpDateValidator";
import "./CardForm.css";

const minLengthValidator = minLength => {
  return value => {
    if (value.length < minLength) {
      return { showError: true, errorMsg: undefined };
    }
    return { showError: false, errorMsg: undefined };
  };
};

export class CardForm extends React.Component {
  constructor(props) {
    super(props);
    const { charge } = props;
    this.refList = {
      expDate: React.createRef(),
      cvv: React.createRef(),
      cardHolderName: React.createRef()
    };

    this.fields = {
      cardNumber: {
        validator: CardInputValidator,
        minLength: 19,
        maxLength: 19,
        nextRef: this.refList.expDate
      },
      expDate: {
        validator: ExpDateValidator,
        minLength: 7,
        maxLength: 7,
        nextRef: this.refList.cvv
      },
      cvv: {
        validator: minLengthValidator(3),
        minLength: 3,
        maxLength: 3,
        regex: /^\d*$/,
        nextRef: this.refList.cardHolderName
      },
      cardHolderName: {
        validator: minLengthValidator(2),
        regex: /^[a-zA-Z ]*$/,
        minLength: 2,
        maxLength: 32
      }
    };

    this.state = {
      isTest: charge.testMode,
      cardNumber: { value: "" },
      expDate: { value: "" },
      cvv: { value: "" },
      cardHolderName: { value: "" },
      saveCard: {
        value: !!charge.customerId
      }
    };
  }

  handleChange(field, event) {
    const { value } = event.target;
    const { maxLength, nextRef, regex } = this.fields[field];
    if (regex && !regex.test(value)) return;
    if (value.length > maxLength) return;
    if (value.length === maxLength) {
      if (this.validate(field, value) && nextRef) {
        nextRef.current.focus();
      }
    } else {
      this.setState({
        [field]: { value, showError: false, errorMsg: undefined }
      });
    }
  }

  handleCheckboxChange(field, event) {
    const { checked } = event.target;
    this.setState({
      [field]: { value: checked }
    });
  }

  handleBlur(field, event) {
    const { value } = event.target;
    if (!this.validate(field, value)) {
      event.preventDefault();
    }
  }

  validate(field, value) {
    const { showError, errorMsg } = this.fields[field].validator(value);
    this.setState({ [field]: { value, showError, errorMsg } });
    return !showError;
  }

  submitCardForm() {
    let invalidForm = false;
    // eslint-disable-next-line no-restricted-syntax,guard-for-in
    for (const field in this.fields) {
      // eslint-disable-next-line react/destructuring-assignment
      if (!this.validate(field, this.state[field].value)) {
        invalidForm = true;
      }
    }
    if (invalidForm) return;

    const { dispatch, charge, history } = this.props;
    const { cardNumber, expDate, cvv, cardHolderName, saveCard } = this.state;
    const expMonth = expDate.value.substr(0, 2);
    const expYear = expDate.value.substr(5);
    const form = {
      cardNumber: cardNumber.value,
      expMonth,
      expYear,
      cvv: cvv.value,
      cardHolderName: cardHolderName.value,
      saveCard: saveCard.value,
      mpiVersion: this.chooseMpiVersion()
    };

    dispatch(fetchRedirectionParams(charge.uuid, form, "card", history));
    form.type = "card";
    dispatch(setCardData(form));
  }

  chooseMpiVersion() {
    // Workaround to force MPIV1 in test mode.
    const { isTest, cardHolderName } = this.state;
    if (isTest) {
      return cardHolderName.value === "PAYZEN MPI" ? 1 : 2;
    }
    return 2;
  }

  selectTestCard(cardNumber) {
    this.setState({
      cardHolderName: { value: "Test Name" },
      cvv: { value: "123" },
      expDate: { value: "12 / 25" },
      cardNumber: { value: cardNumber }
    });
  }

  render() {
    const { charge, redirecting } = this.props;
    const {
      cardNumber,
      expDate,
      cvv,
      cardHolderName,
      isTest,
      saveCard
    } = this.state;

    const canSaveCard = charge.customerId;
    return (
      <Grid container className="card-form">
        <Grid item sm={8} xs={12}>
          <CardInput
            className="card-form-field"
            onChange={e => this.handleChange("cardNumber", e)}
            onBlur={e => this.handleBlur("cardNumber", e)}
            value={cardNumber.value}
            showError={cardNumber.showError}
            errorMsg={cardNumber.errorMsg}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <ExpDateInput
            className="card-form-field"
            onChange={e => this.handleChange("expDate", e)}
            onBlur={e => this.handleBlur("expDate", e)}
            withRef={this.refList.expDate}
            value={expDate.value}
            showError={expDate.showError}
            errorMsg={expDate.errorMsg}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            className="card-form-field"
            id="cvv"
            inputProps={{ name: "cvv" }}
            label="CVV"
            style={{ width: "33%" }}
            type="password"
            value={cvv.value}
            error={cvv.showError}
            helperText={cvv.errorMsg}
            onChange={e => this.handleChange("cvv", e)}
            onBlur={e => this.handleBlur("cvv", e)}
            inputRef={this.refList.cvv}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            className="card-form-field"
            id="cardHolderName"
            inputProps={{ name: "cardHolderName" }}
            label="Card Holder Name"
            type="text"
            fullWidth
            value={cardHolderName.value}
            error={cardHolderName.showError}
            helperText={cardHolderName.errorMsg}
            onChange={e => this.handleChange("cardHolderName", e)}
            onBlur={e => this.handleBlur("cardHolderName", e)}
            inputRef={this.refList.cardHolderName}
          />
        </Grid>
        {canSaveCard && (
          <Grid item xs={12}>
            <FormControlLabel
              label="Save card details for next time"
              control={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <Switch
                  id="saveCard"
                  color="primary"
                  checked={saveCard.value}
                  value="saveCard"
                  onChange={e => this.handleCheckboxChange("saveCard", e)}
                />
              }
            />
          </Grid>
        )}
        <input type="hidden" name="uuid" value={charge.uuid} />

        {isTest && (
          <Grid item xs={12}>
            <FormControl style={{ width: "100%" }} className="card-form-field">
              <InputLabel htmlFor="testCard">Test card</InputLabel>
              <Select
                id="testCard"
                value={cardNumber.value}
                onChange={e => {
                  this.selectTestCard(e.target.value);
                }}
              >
                {TestCards.map(card => {
                  return (
                    <MenuItem key={card.cardNumber} value={card.cardNumber}>
                      {card.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} style={{ textAlign: "center" }}>
          <Button
            id="pay-button"
            variant="outlined"
            color="primary"
            disabled={redirecting}
            onClick={() => this.submitCardForm()}
          >
            {redirecting ? (
              <CircularProgress color="inherit" size={30} />
            ) : (
              `Pay ${FormatAmount(charge.amount, charge.currency)}`
            )}
          </Button>
        </Grid>
      </Grid>
    );
  }
}

CardForm.propTypes = {
  charge: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    testMode: PropTypes.bool.isRequired,
    customerId: PropTypes.string,
    amount: PropTypes.number,
    currency: PropTypes.string
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  redirecting: PropTypes.bool.isRequired,
  history: PropTypes.shape().isRequired
};

function mapStateToProps({ redirectionReducer }) {
  return redirectionReducer;
}

export default withRouter(connect(mapStateToProps)(CardForm));
